import { defineComponent as _defineComponent } from 'vue'
import Tabs from '@/components/basics/Tabs.vue';
import LoadingAnimationSmall from '@/modules/shared/components/loading-animation/LoadingAnimationSmall.vue';
import type { Dashboard, WidgetConfig, WidgetTemplate } from '@/modules/ctx-dashboard';
import ChartPreview from '@/components/illustrations/chart-previews/ChartPreview.vue';
import NotificationService from '@/assets/js/services/NotificationService';
import InputCheckbox from '@/modules/shared/components/form/InputCheckbox.vue';
import { Port as SharedPort } from '@/modules/shared/Port';
import { Port } from '@/modules/ctx-dashboard/Port';
import NoteBox from '@/components/basics/NoteBox.vue';
import ArrayUtils from '@/assets/js/utils/ArrayUtils';
import ConfirmDlg from '@/modules/shared/components/dialogs/generic/ConfirmDlg.vue';
import { computed, onMounted, ref, withDefaults, nextTick, watch } from 'vue';

type Props = {
    portfolioKey: string|null;
    dashboardKey: string|null;
    allTabsValid?: boolean;
    skipValidWizardTabs?: boolean;
}
type Emits = {
    (e: 'start'): void;
    (e: 'save'): void;
    (e: 'cancel'): void;
    (e: 'update:widget', widget: WidgetConfig | null): void;
    (e: 'update:skipValidWizardTabs', skipValidWizardTabs: boolean): void;
}

export default /*#__PURE__*/_defineComponent({
  __name: 'PresetSelection',
  props: {
    portfolioKey: null,
    dashboardKey: null,
    allTabsValid: { type: Boolean, default: false },
    skipValidWizardTabs: { type: Boolean, default: false }
  },
  emits: ["start", "save", "cancel", "update:widget", "update:skipValidWizardTabs"],
  setup(__props: any, { emit: emits }: { emit: ({
    (e: 'start'): void;
    (e: 'save'): void;
    (e: 'cancel'): void;
    (e: 'update:widget', widget: WidgetConfig | null): void;
    (e: 'update:skipValidWizardTabs', skipValidWizardTabs: boolean): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { portfolioKey: string|null, dashboardKey: string|null, allTabsValid: boolean, skipValidWizardTabs: boolean };




const templates = ref<WidgetTemplate[]>([]);
const selectedTemplate = ref<WidgetTemplate|null>(null);
const categories = ref<string[]>([]);
const selectedCategory = ref<string>('');
const dashboard = ref<Dashboard|null>(null);
const widget = ref<WidgetConfig|null>(null);
const loading = ref<boolean>(false);
const saving = ref<boolean>(false);
const loadingWidget = ref<Promise<WidgetConfig|null>|null>(null);
const showDeleteConfirmDlg = ref<boolean>(false);

const insertAtEnd = computed<boolean>(() => SharedPort.userSettings.getUserSettings().insertNewWidgetsPosition === 'end');
const saveActionBlocked = computed<boolean>(() => loading.value || saving.value || selectedTemplate.value === null);
const templatesForCategory = computed<WidgetTemplate[]>(() => templates.value.filter((t) => t.category === selectedCategory.value));

function onCategoryChanged(): void {
    SharedPort.userSettings.updateUserSettings({ presetCategory: selectedCategory.value });
}
function setInsertAtEnd(updatedInsertAtEnd: boolean) {
    if (updatedInsertAtEnd) {
        SharedPort.userSettings.updateUserSettings({ insertNewWidgetsPosition: 'end' });
    } else {
        SharedPort.userSettings.updateUserSettings({ insertNewWidgetsPosition: 'start' });
    }
}
async function selectTemplate(template: WidgetTemplate): Promise<void> {
    selectedTemplate.value = template;
    loadingWidget.value = getWidgetFromTemplate(template);
    await loadingWidget.value;
    emits('update:widget', widget.value);
    emits('update:skipValidWizardTabs', !selectedTemplate.value.isDefaultTemplate);
}
function selectFirst() {
    if (templatesForCategory.value[0]) {
        selectTemplate(templatesForCategory.value[0]);
    } else {
        selectedTemplate.value = null;
    }
}
async function getWidgetFromTemplate(template: WidgetTemplate): Promise<WidgetConfig|null> {
    if (!props.portfolioKey || !props.dashboardKey) return null;

    try {
        const widgetConfig: WidgetConfig = await Port.widgets.getWidgetFromTemplate(template, props.portfolioKey, props.dashboardKey);
        widget.value = widgetConfig;
        return widgetConfig;
    } catch (e: any) {
        NotificationService.serviceError(e);
        return null;
    }
}
async function deleteWidgetTemplate(template: WidgetTemplate | null): Promise<void> {
    if (!template) return;

    try {
        loading.value = true;
        await Port.widgets.deleteWidgetTemplate(template);
        await fetchTemplates();
        showDeleteConfirmDlg.value = false;
        loading.value = false;
    } catch (e: any) {
        NotificationService.serviceError(e);
    }
}
async function fetchDashboard(): Promise<void> {
    if (!props.dashboardKey) return;

    dashboard.value = await Port.dashboards.getDashboard(props.dashboardKey);
}
async function fetchTemplates(): Promise<void> {
    templates.value = await Port.widgets.getWidgetTemplates();
    categories.value = templates.value
        .map((t) => t.category)
        .filter(ArrayUtils.removeDuplicates);
    selectedCategory.value = SharedPort.userSettings.getUserSettings().presetCategory;
    if (!selectedCategory.value || !categories.value.includes(selectedCategory.value)) {
        selectedCategory.value = categories.value[0];
    }
    selectFirst();
}
async function next(): Promise<void> {
    saving.value = true;
    if (loadingWidget.value) {
        await loadingWidget.value;
    }
    emits('start');
}
async function save(): Promise<void> {
    saving.value = true;
    if (loadingWidget.value) {
        await loadingWidget.value;
    }
    emits('save');
}
function cancel() {
    emits('cancel');
}

watch(() => selectedCategory.value, onCategoryChanged);

onMounted(async () => {
    loading.value = true;
    await Promise.all([
        fetchDashboard(),
        fetchTemplates(),
    ]);
    loading.value = false;
    nextTick(selectFirst);
});

return { __sfc: true,props, emits, templates, selectedTemplate, categories, selectedCategory, dashboard, widget, loading, saving, loadingWidget, showDeleteConfirmDlg, insertAtEnd, saveActionBlocked, templatesForCategory, onCategoryChanged, setInsertAtEnd, selectTemplate, selectFirst, getWidgetFromTemplate, deleteWidgetTemplate, fetchDashboard, fetchTemplates, next, save, cancel, Tabs, LoadingAnimationSmall, ChartPreview, InputCheckbox, NoteBox, ConfirmDlg }
}

})